<template>
    <div class="tile-content tile-content-secondary">
        <split-test
            :class="{'stack-m': !requiresTabs}"
            :versions="action.bumpOffers"
            :is-form-updating="isFormUpdating"
            :delete-allowed="globalDeleteAllowed"
            :validate="$v.action.bumpOffers"
            @add-version="addVersion"
            @pick-version="pickVersion"
            @delete-version="deleteVersion"
            @update-weight="updateWeight"
            @update-name="updateName"
            editable-name
            can-duplicate
        />
        <div class="stack-l">
            <div class="divider divider-tile-content"></div>
        </div>
        <error-messages
            :server-error="serverError"
            :offers="action.bumpOffers"
            :validate="$v.action.bumpOffers"/>
        <!-- two ore more emails -->
        <template v-if="requiresTabs">
            <r-tabs :divided="true">
                <r-tab v-for="(offer, tabIndex) in action.bumpOffers" :key="tabIndex" :name="offer.name">
                    <offer-editor
                        :offer="offer"
                        :index="tabIndex"
                        :validation-iteratee="$v.action.bumpOffers.$each.$iter"
                        @input="updateOffer"/>
                </r-tab>
            </r-tabs>
        </template>
        <!-- only one email -->
        <div v-else>
            <offer-editor
                :offer="firstOffer"
                :index="0"
                :validation-iteratee="$v.action.bumpOffers.$each.$iter"
                @input="updateOffer"/>
        </div>
    </div>
</template>

<script>
    import {vSelect} from '@/components/form';
    import {required, between, minValue} from 'vuelidate/lib/validators';
    import SplitTest from '@/modules/rules-engine/actions/send-email/components/split-test';
    import {BumpOfferModel} from '@/common/models/rules/actions/offer-purchase-bump-action-model';
    import OfferEditor from '@/modules/rules-engine/actions/offer-purchase-bump/components/offer-editor';
    import ErrorMessages from '@/modules/rules-engine/actions/offer-purchase-bump/components/error-messages';
    import BaseAction from '../base-action';

    const minOneWeightedOffer = function minOneWeightedOffer() {
        if (this.$v.action.$anyDirty) {
            return this.action.bumpOffers
                .some(e => (isNaN(e.weight) ? 0 : Number(e.weight)) > 0);
        }
        return true;
    };

    const isAmount = function isAmount(value) {
        return !isNaN(Number(value));
    };

    export default {
        name: 'offer-purchase-bump',
        extends: BaseAction,
        components: {
            vSelect,
            SplitTest,
            OfferEditor,
            ErrorMessages,
        },
        computed: {
            requiresTabs() {
                return this.action.bumpOffers.length > 1;
            },
            globalDeleteAllowed() {
                return this.newActionPredicate(this.action);
            },
            firstOffer() {
                return this.action.bumpOffers[0];
            },
            hasCombinedValidationResults() {
                return this.validationResults.length > 0;
            },
        },
        validations: {
            action: {
                bumpOffers: {
                    minOneWeightedOffer,
                    $each: {
                        weight: {
                            between: between(0, 100),
                            minOneWeightedOffer,
                        },
                        bumpAmount: {required, isAmount, minValue: minValue(0)},
                        bonusAmount: {required, isAmount, minValue: minValue(0)},
                        choices: {
                            $each: {
                                content: {required},
                            },
                        },
                    },
                },
            },
        },
        data() {
            return {
                validationResults: [],
                serverError: null,
                relativeVersionNumber: this.action.bumpOffers.length,
            };
        },
        methods: {
            addVersion({version} = {}) {
                const copy = this.action.cloneSelf();
                if (version) {
                    const {name} = version;
                    copy.bumpOffers.push(new BumpOfferModel({
                        ...version,
                        name: this.action.getCopyBumpOfferName(name),
                    }));
                } else {
                    this.relativeVersionNumber += 1;
                    copy.bumpOffers.push(new BumpOfferModel({name: `Version ${this.relativeVersionNumber}`}));
                }
                copy.recalculateWeights();
                this.dispatchUpdate(copy);
            },
            spliceOffers(index, injection) {
                const copy = this.action.cloneSelf();
                if (injection) {
                    copy.bumpOffers.splice(index, 1, injection);
                } else {
                    copy.bumpOffers.splice(index, 1);
                }
                copy.recalculateWeights();
                this.dispatchUpdate(copy);
            },
            deleteVersion(index) {
                this.spliceOffers(index);
            },
            pickVersion(winnerIndex) {
                const copy = this.action.cloneSelf();
                copy.bumpOffers = copy.bumpOffers.splice(winnerIndex, 1);
                this.dispatchUpdate(copy);
            },
            updateWeight({index, weight}) {
                const copy = this.action.cloneSelf();
                copy.bumpOffers[index].weight = weight;
                this.dispatchUpdate(copy);
            },
            updateName({index, name}) {
                const copy = this.action.cloneSelf();
                copy.bumpOffers[index].name = name;
                this.dispatchUpdate(copy);
            },
            updateOffer({index, offer}) {
                this.spliceOffers(index, offer);
            },
            dispatchUpdate(data) {
                this.$emit('input', data);
            },
            getValidationAggregate() {
                return {
                    name: 'offer-purchase-bump',
                    beforeValidation: () => this.$v.$touch(),
                    rules: [
                        {
                            name: `Please verify that all required bump offer fields are provided.`,
                            delegate: () => this.$v.action.bumpOffers.$error,
                        },
                        {
                            name: `At least one bump offer version in the split test must have a weight greater than zero.`,
                            delegate: () => !this.$v.action.bumpOffers.minOneWeightedOffer,
                        },
                    ],
                };
            },
        },
    };
</script>
